/* eslint-disable max-len */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Reference as IAMReference } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/Reference';
import { RootState } from 'app/AppStore';
import { ReactComponent as MotrioLogo } from 'assets/CarBrand/brand_motrio_mini.svg';
import { ReactComponent as ValuePlusLogo } from 'assets/CarBrand/brand_value_plus_mini.svg';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import { CopyReferenceWithFeedback, CopySelectionWrapper } from 'components/CopyReferenceFeedback';
import { DataContainer } from 'components/DataContainer';
import { DiscountCorner } from 'components/Discount';
import { DocumentationAlertButton } from 'components/DocumentationAlertButton';
import { DocumentScrollModal } from 'components/DocumentScrollModal/DocumentScrollModal';
import ProductModal from 'components/ProductModal';
import { ReferencePriceSection } from 'components/ReferencePriceSection';
import StockDisplay from 'components/StockInfo';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { IAM, OEM_BRAND_KEY, STANDARD } from 'domains/catalog/Catalog.types';
import { getIAMReference, ReferencePriceType } from 'domains/references';
import { isReferenceExadis, isRefMKTP } from 'domains/references/References.utils';
import { GarageView, getIamCatalogBrandsView, SparePartsViewType } from 'domains/user';
import {
  AdditionalInformationButton,
  ProductPictureButton,
} from 'pages/CatalogPage/common/ReferenceSection/ReferenceSectionButtons';
import { SupersessionTooltip } from 'pages/CatalogPage/common/ReferenceSection/SupersessionTooltip';
import {
  handleAddToEstimate,
  STANDARD_EXCHANGE_REFERENCE_PATTERN,
} from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/IAMReferenceCard';
import { SCard } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/IAMReferenceCard.styled';

import {
  Box,
  Checkbox,
  Descriptions,
  Flex,
  Icon,
  INITIAL_ITEM_COUNT,
  Item,
  LinkRoundButton,
  MarginBox,
  Text,
} from 'UI';
import { getData, hasPrice } from 'utils';

interface ReferenceCardProps {
  reference: IAMReference;
  rawLabels: string[];
  additionalInformationItems: Item[];
  price?: ReferencePriceType;
  sparePartsView: SparePartsViewType;
  handleAddToCart: (sellerName?: string, sellerNumber?: string) => void;
  handleClickCompare?: (reference: string) => void;
  selected?: boolean;
  onSelect?: (referenceNumber: string) => void;
  bordered?: boolean;
}

const IAMReferenceCardCompact = ({
  reference,
  rawLabels,
  additionalInformationItems,
  price,
  sparePartsView,
  selected,
  onSelect,
  handleAddToCart,
  handleClickCompare,
  bordered = true,
}: ReferenceCardProps) => {
  const {
    referenceNumber,
    referenceSource,
    supplier,
    supplierCode,
    origin,
    documents,
    brand,
    replacingReferences,
    substitutedReferences,
  } = reference;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const detailWithVehicle = useSelector((state: RootState) =>
    getIAMReference(state, { vehicleKey: vehicleDetail?.vehicleKey, referenceNumber }),
  );
  const detailWithoutVehicle = useSelector((state: RootState) =>
    getIAMReference(state, { vehicleKey: undefined, referenceNumber }),
  );
  const detail = detailWithVehicle ?? detailWithoutVehicle;
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const displayShowMoreButton = additionalInformationItems.length > INITIAL_ITEM_COUNT;
  const displaySupersessionTooltip =
    (replacingReferences && replacingReferences.length > 0) ||
    (substitutedReferences && substitutedReferences.length > 0);
  const initialItems = additionalInformationItems.slice(0, INITIAL_ITEM_COUNT);
  const [currentData, setCurrentData] = useState(initialItems);
  const displayName = reference.name ?? reference.designation ?? '';
  const translatedName = referenceNumber.startsWith(STANDARD_EXCHANGE_REFERENCE_PATTERN)
    ? `${displayName} [${t('catalog.iam.standard_exchange', 'STANDARD EXCHANGE')}]`
    : displayName;
  const translatedDisplayName = [translatedName, supplier, ...rawLabels].filter(Boolean).join(' / ');
  const iamCatalogBrandsView = useSelector(getIamCatalogBrandsView);
  const isOEM = iamCatalogBrandsView === OEM_BRAND_KEY;

  const handleShowMore = () => {
    setCurrentData(showMore ? initialItems : [...additionalInformationItems]);
    setShowMore((prevState) => !prevState);
  };

  const renderPriceSection = () => {
    return (
      <ReferencePriceSection
        referenceNumber={referenceNumber}
        sparePartsView={sparePartsView}
        handleAddToCartClick={handleAddToCart}
        handleClickCompare={handleClickCompare}
        vehicleKey={vehicleDetail?.vehicleKey}
        align={'left'}
        catalogSource={IAM}
        referenceType={STANDARD}
        narrow
        useCompactView
        origin={origin}
        referenceSource={referenceSource}
        supplierCode={supplierCode}
        isMKTP={isRefMKTP(getData(detail))}
        isExadis={isReferenceExadis(IAM, reference.brand, origin)}
      />
    );
  };

  const handleAddToEstimateClick = (quantity?: number) => {
    handleAddToEstimate(dispatch, history, {
      referenceNumber,
      displayName,
      price,
      brand,
      origin,
      supplierCode,
      referenceSource,
      quantity,
    });
  };

  const renderEstimateSection = () =>
    hasPrice(sparePartsView, price) && !isOEM ? (
      <AddToEstimateButtonAndDialog handleAddToEstimateClick={handleAddToEstimateClick} size={'lg'} isVehicleCatalog />
    ) : null;

  const getReferenceLogo = () => {
    switch (supplier) {
      case 'MOTRIO':
        return <Icon IconComponent={MotrioLogo} width={70} />;
      case 'VALUE_PLUS':
        return <Icon IconComponent={ValuePlusLogo} width={70} />;
      default:
        return <></>;
    }
  };

  const [viewAdditionalInfo, setViewAdditionalInfo] = useState<boolean>(false);

  function handleInfoButtonClick() {
    setViewAdditionalInfo(!viewAdditionalInfo);
  }

  function handleProductPictureButtonClick() {
    setDocumentsModalVisible(!documentsModalVisible);
  }

  function handleCheckboxClick() {
    onSelect && onSelect(referenceNumber);
  }

  return (
    <DataContainer data={detail}>
      <SCard isCompact bordered={bordered}>
        {sparePartsView === GarageView && <DiscountCorner reference={referenceNumber} type={'catalog_mini'} />}
        <Flex direction={'row'} align={'center'} minHeight={30}>
          <Box width={24} />
          <Flex direction={'column'}>
            <Flex align={'center'} minWidth={200}>
              <Checkbox checked={selected} onChange={handleCheckboxClick} />
              <Box width={20} />
              <ProductModal
                triggerComponent={
                  <CopySelectionWrapper referenceNumber={referenceNumber}>
                    <Text type="base" bold disableGutter hoverUnderLine cursor={'pointer'}>
                      {referenceNumber}
                    </Text>
                  </CopySelectionWrapper>
                }
                referenceNumber={referenceNumber}
                isVehicleCatalog
              />
              <Box width={16} />
              <CopyReferenceWithFeedback referenceNumber={referenceNumber} />
              <Box width={16} />
              <Box width={24}>
                {displaySupersessionTooltip && (
                  <SupersessionTooltip>
                    <LinkRoundButton
                      onClick={() => {
                        handleInfoButtonClick();
                        displayShowMoreButton && handleShowMore();
                      }}
                    />
                  </SupersessionTooltip>
                )}
              </Box>
              <Box width={8} />
              <Box width={24}>
                {!isOEM && (
                  <>
                    <MarginBox mr={1} />
                    <DocumentationAlertButton
                      reference={getData(detail)}
                      vehicleRegistrationNumber={vehicleDetail?.vrn ?? vehicleDetail?.vehicleKey}
                      vehicleDetail={vehicleDetail}
                    />
                  </>
                )}
              </Box>
              <Box width={40} />
              <Text type="base" bold>
                {translatedDisplayName}
              </Text>
            </Flex>
          </Flex>
          <Box align={'center'} width={70}>
            {getReferenceLogo()}
          </Box>
          <Flex />
          <Flex align={'center'} maxWidth={600}>
            <MarginBox mx={15}>
              <DataContainer data={additionalInformationItems} NotFound={() => <Box width={40} />}>
                {additionalInformationItems.length > 0 ? (
                  <AdditionalInformationButton isOpen={viewAdditionalInfo} handleClick={handleInfoButtonClick} />
                ) : (
                  <Box width={40} />
                )}
              </DataContainer>
            </MarginBox>
            <MarginBox mx={15}>
              {documents && documents.length > 0 ? (
                <ProductPictureButton isOpen={documentsModalVisible} handleClick={handleProductPictureButtonClick} />
              ) : (
                <Box width={40} />
              )}
            </MarginBox>
            <Box width={10} />
            <Box width={20}>
              <StockDisplay
                vehicleKey={vehicleDetail?.vehicleKey}
                referenceNumber={referenceNumber}
                isApplicableToCurrentVehicle
                small
                spinSize={'default'}
              />
            </Box>
            <Box width={10} />
            <Box width={300}>{renderPriceSection()}</Box>
            <MarginBox mx={25}>
              <Box width={50}>{renderEstimateSection()}</Box>
            </MarginBox>
          </Flex>
        </Flex>
        {viewAdditionalInfo && additionalInformationItems.length > 0 && (
          <Descriptions
            title={t('catalog.details.additional_information', 'Additional information')}
            items={currentData}
            displayShowMoreButton={displayShowMoreButton}
            showMore={showMore}
            handleShowMore={handleShowMore}
          />
        )}
        <DocumentScrollModal
          documents={documents ?? []}
          visible={documentsModalVisible}
          onVisibleChange={(visible) => setDocumentsModalVisible(visible)}
        >
          {undefined}
        </DocumentScrollModal>
      </SCard>
      <MarginBox mt={15} />
    </DataContainer>
  );
};

export default IAMReferenceCardCompact;
